/* Promotion panel — hosted release-based config promotion (dev -> test -> prod).

   Source is the ACTIVE connection (api.connectionMeta). The flow is:
     1. Snapshot the source  -> a portable Release (release_id + asset_count), via
        api.promotionSnapshot(sourceCid).
     2. Pick a TARGET connection (same family, a higher env rung) and Plan -> a dry-run
        diff grouped by action (create/update/in_sync), via api.promotionPlanHosted(targetCid, rid).
     3. Apply -> the gated real run record (status + per-asset applied/failed), via
        api.promotionApplyHosted(targetCid, rid, {confirm}). A PROD target requires the target
        label typed verbatim before Apply enables (mirrors the server's confirmation gate).
   History lists the family's releases (api.promotionReleases); clicking one selects it so an
   existing release can be planned/applied without re-snapshotting.

   Hosted-only: the API is per-connection and registered only in hosted mode. In local/desktop
   mode api.connectionMeta() is null, so the panel renders a clear hosted-only empty state. */

import { esc, setPanelLoading } from "./state.js";
import { api } from "./api.js";
import { openUndoDialog } from "./rewind-dialog.js";

// dev < test < prod — a release may only be promoted strictly UP the rung (mirrors the server's
// allow_promotion gate). Unknown/absent roles sort as null and are never eligible targets.
const RUNG = { dev: 0, test: 1, prod: 2 };

// Plan action -> icon mask name (icons.css) + label. Mirrors pipelines.js _ACTION_ICON.
const ACTION_ICON  = { create: "plus", update: "edit", delete: "x", in_sync: "check" };
const ACTION_LABEL = { create: "create", update: "update", delete: "delete", in_sync: "in sync" };

// Run-result per-asset status -> icon mask name. Mirrors promotion-drawer.js _GLYPH.
const RESULT_ICON = {
  applied: "check", in_sync: "check", failed: "x", blocked: "circle", skipped: "circle",
};

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _source = null;          // active connection meta {id, label, env_role, family}
let _targets = [];           // eligible target connections
let _selectedTargetId = "";  // chosen target connection id
let _releases = [];          // family release history [{release_id, asset_count, created_at, source_tenant}]
let _release = null;         // active release for plan/apply {release_id, asset_count}
let _plan = null;            // last plan result {plan:[...], dry_run}
let _runRec = null;          // last apply run record {status, results:[...], run_id}
let _confirmText = "";       // typed prod-confirm value (preserved across re-renders)
let _busy = "";              // "snapshot" | "plan" | "apply" | "" — gates the action buttons
let _error = "";             // inline error banner text (4xx gate/lineage reasons)

// ---------------------------------------------------------------------------
// Container helper
// ---------------------------------------------------------------------------

function _container() {
  return document.getElementById("panel-promotion");
}

function _body() {
  const el = _container();
  return el ? el.querySelector("#promotionBody") : null;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initPromotionPanel() {
  const el = _container();
  if (!el || el._promWired) return;
  el._promWired = true;
  el.addEventListener("click", _onClick);
  el.addEventListener("change", _onChange);
  el.addEventListener("input", _onInput);
}

// v2 shell (hosted): mount this release-based flow into an arbitrary container, wrapped in
// the native v2 chrome every other view uses (.content-head header + .vpad body). The inner
// markup keeps this module's own ids (#panel-promotion for the delegated handlers,
// #promotionBody for the render target, #promRefresh caught by the same _onClick), so all
// the flow logic above runs unchanged. v1's zone panel builds the same ids via
// _populatePromotionPanel() (main.js) and is untouched.
export function renderPromotionHostedInto(mount) {
  if (!mount) return;
  mount.innerHTML = `
    <div id="panel-promotion">
      <div class="content-head">
        <span class="ch-title">Promotion</span>
        <span class="ch-sub">Move a release up the ladder (dev, test, prod)</span>
        <div class="ch-right">
          <button type="button" class="btn" id="promRefresh">
            <span class="ic ic-sm ic-refresh-cw"></span> Refresh
          </button>
        </div>
      </div>
      <div class="vpad"><div id="promotionBody"></div></div>
    </div>`;
  initPromotionPanel();
  onPromotionActivated();
}

export function onPromotionActivated() {
  refreshPromotion();
}

export async function refreshPromotion() {
  const body = _body();
  if (!body) return;

  setPanelLoading("promotion", true);
  _error = "";
  try {
    // Source = the active connection. Null in local/desktop mode -> hosted-only empty state.
    _source = await api.connectionMeta();
    if (!_source) {
      _targets = []; _releases = [];
      body.innerHTML = _renderEmpty();
      return;
    }
    // Eligible targets + the family's release history, in parallel (both best-effort).
    const [connsData, relData] = await Promise.all([
      api.connections().catch(() => ({ connections: [] })),
      api.promotionReleases().catch(() => ({ releases: [] })),
    ]);
    const all = (connsData && connsData.connections) || [];
    _targets = _eligibleTargets(_source, all);
    _releases = (relData && relData.releases) || [];
    // Keep the current target selection only if it's still eligible.
    if (!_targets.some((t) => t.id === _selectedTargetId)) {
      _selectedTargetId = _targets.length ? _targets[0].id : "";
    }
    body.innerHTML = _render();
  } catch (e) {
    _error = String((e && e.message) || e);
    body.innerHTML = _render();
  } finally {
    setPanelLoading("promotion", false);
  }
}

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

function _rung(role) {
  const r = RUNG[String(role || "").toLowerCase()];
  return r === undefined ? null : r;
}

// Targets in the SAME family and a STRICTLY HIGHER env rung than the source.
function _eligibleTargets(source, all) {
  if (!source || !source.family) return [];
  const srcRung = _rung(source.env_role);
  if (srcRung === null) return [];
  return all.filter((c) => {
    if (!c || c.id === source.id) return false;
    if (!c.family || c.family !== source.family) return false;
    const r = _rung(c.env_role);
    return r !== null && r > srcRung;
  });
}

function _selectedTarget() {
  return _targets.find((t) => t.id === _selectedTargetId) || null;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function _envBadge(role) {
  const r = String(role || "").toLowerCase();
  const cls = (r === "prod" || r === "test" || r === "dev") ? r : "none";
  return `<span class="prom-badge prom-badge-${esc(cls)}">${esc(r || "no env")}</span>`;
}

function _renderEmpty() {
  return `<div class="prom-empty">
    <div class="prom-empty-title">Promotion is hosted-only</div>
    <div class="prom-empty-sub">Pick a connection to promote from. Promotion moves a config
      release from one SAP CPQ connection up the environment ladder (dev &rarr; test &rarr; prod)
      across connections in the same family.</div>
  </div>`;
}

function _render() {
  const errBanner = _error ? `<div class="prom-error">${esc(_error)}</div>` : "";

  // Source header
  const srcHtml = `<div class="prom-source">
    <span class="prom-source-label" title="${esc(_source.label || "")}">${esc(_source.label || "Connection")}</span>
    ${_envBadge(_source.env_role)}
    ${_source.family ? `<span class="prom-fam">${esc(_source.family)}</span>` : `<span class="prom-fam prom-fam-none">no family</span>`}
  </div>`;

  // Target picker
  let targetHtml;
  if (!_source.family) {
    targetHtml = `<div class="prom-note">Assign this connection a <b>family</b> (in Add a connection)
      to form a promotion pipeline.</div>`;
  } else if (_targets.length === 0) {
    targetHtml = `<div class="prom-note">No eligible target (same family, higher environment).</div>`;
  } else {
    const opts = _targets.map((t) =>
      `<option value="${esc(t.id)}"${t.id === _selectedTargetId ? " selected" : ""}>${esc(t.label)} · ${esc(String(t.env_role || ""))}</option>`
    ).join("");
    targetHtml = `<div class="prom-route">
      <span class="ic ic-sm ic-arrow-right prom-route-arrow" aria-hidden="true"></span>
      <select id="promTarget" class="prom-select" title="Promotion target">${opts}</select>
    </div>`;
  }

  // Release line
  const relHtml = _release
    ? `<div class="prom-rel">
        <span class="ic ic-sm ic-layers" aria-hidden="true"></span>
        <span class="prom-rel-id pl-mono">${esc(_release.release_id)}</span>
        <span class="prom-rel-count">${esc(String(_release.asset_count ?? 0))} asset${_release.asset_count === 1 ? "" : "s"}</span>
      </div>`
    : `<div class="prom-rel prom-rel-none">No snapshot yet.</div>`;

  // Actions
  const target = _selectedTarget();
  const isProd = !!(target && _rung(target.env_role) === RUNG.prod);
  const canSnapshot = !!_source.family && _busy === "";
  const canPlan = !!_release && !!target && _busy === "";
  const confirmOk = !isProd || (_confirmText.trim() === (target ? target.label : ""));
  const canApply = !!_release && !!target && confirmOk && _busy === "";

  const actionsHtml = `<div class="prom-actions">
    <button id="promSnapshot" class="prom-btn"${canSnapshot ? "" : " disabled"}>
      <span class="ic ic-sm ic-layers" aria-hidden="true"></span> Snapshot source
    </button>
    <button id="promPlan" class="prom-btn"${canPlan ? "" : " disabled"}>
      <span class="ic ic-sm ic-eye" aria-hidden="true"></span> Plan
    </button>
    <button id="promApply" class="prom-btn prom-btn-cta"${canApply ? "" : " disabled"}>
      <span class="ic ic-sm ic-arrow-right" aria-hidden="true"></span> Apply
    </button>
  </div>`;

  // Prod typed-confirm — only when the selected target is prod.
  const confirmHtml = isProd
    ? `<div class="prom-confirm-row">
        <span class="ic ic-sm ic-lock prom-confirm-ic" aria-hidden="true"></span>
        <input id="promConfirm" class="prom-confirm-input" type="text" autocomplete="off"
          placeholder="${esc(target.label)}" value="${esc(_confirmText)}"
          title="Type the target label to confirm a prod promotion" />
        <span class="prom-confirm-hint">type <b>${esc(target.label)}</b> to confirm</span>
      </div>`
    : "";

  return `${errBanner}${srcHtml}${targetHtml}${actionsHtml}${relHtml}${confirmHtml}
    <div class="prom-stage">${_renderStage()}</div>
    ${_renderHistory()}`;
}

function _renderStage() {
  if (_busy === "snapshot") return `<div class="prom-loading">Snapshotting source…</div>`;
  if (_busy === "plan")     return `<div class="prom-loading">Computing plan…</div>`;
  if (_busy === "apply")    return `<div class="prom-loading">Applying…</div>`;
  if (_runRec) return _renderResult(_runRec);
  if (_plan)   return _renderPlan(_plan);
  return "";
}

function _renderPlan(plan) {
  const items = Array.isArray(plan.plan) ? plan.plan : [];
  if (items.length === 0) {
    return `<div class="prom-stage-empty">In sync. Nothing to promote.</div>`;
  }
  // Counts grouped by action (create/update/in_sync/delete).
  const counts = {};
  for (const p of items) counts[p.action] = (counts[p.action] || 0) + 1;
  const order = ["create", "update", "delete", "in_sync"];
  const summary = order.filter((a) => counts[a]).map((a) =>
    `<span class="prom-count prom-count-${esc(a)}">${counts[a]} ${esc(ACTION_LABEL[a] || a)}</span>`
  ).join("");

  const rows = items.map((p) => {
    const a = String(p.action || "");
    const icon = ACTION_ICON[a] || "circle";
    const advisory = p.detail
      ? `<span class="ic ic-sm ic-alert-triangle prom-advisory" title="${esc(p.detail)}" aria-label="advisory"></span>`
      : "";
    return `<div class="prom-row prom-act-${esc(a)}">
      <span class="ic ic-sm ic-${icon} prom-glyph" aria-hidden="true"></span>
      <span class="prom-name pl-mono">${esc(p.name || "")}</span>
      <span class="prom-type">${esc(p.type || "")}</span>
      <span class="prom-act-label">${esc(ACTION_LABEL[a] || a)}</span>${advisory}
    </div>`;
  }).join("");

  return `<div class="prom-stage-head">Plan <span class="prom-stage-sub">(dry run)</span></div>
    <div class="prom-summary">${summary}</div>
    <div class="prom-list">${rows}</div>`;
}

function _renderResult(rec) {
  const results = Array.isArray(rec.results) ? rec.results : [];
  const status = String(rec.status || "");
  const statusCls = status === "ok" ? "ok"
    : status === "partial" ? "warn"
    : status === "empty" ? "muted"
    : "err";
  const head = `<div class="prom-stage-head">Result
    <span class="prom-status prom-status-${statusCls}">${esc(status || "done")}</span>
    ${rec.run_id ? `<span class="prom-runid pl-mono" title="run id">${esc(rec.run_id)}</span>` : ""}
    ${rec.run_id ? `<button id="promUndoRun" class="prom-btn prom-undo-run" title="Undo every change this run made">
      <span class="ic ic-sm ic-undo" aria-hidden="true"></span> Undo this run
    </button>` : ""}
  </div>`;
  if (results.length === 0) {
    return `${head}<div class="prom-stage-empty">No assets changed.</div>`;
  }
  const rows = results.map((r) => {
    const st = String(r.status || "");
    const icon = RESULT_ICON[st] || "circle";
    const errLine = r.error ? `<div class="prom-err-line">${esc(r.error)}</div>` : "";
    return `<div class="prom-row prom-res-${esc(st)}">
      <span class="ic ic-sm ic-${icon} prom-glyph" aria-hidden="true"></span>
      <span class="prom-name pl-mono">${esc(r.name || "")}</span>
      <span class="prom-type">${esc(r.type || "")}</span>
      <span class="prom-act-label">${esc(st)}</span>
      ${errLine}
    </div>`;
  }).join("");
  return `${head}<div class="prom-list">${rows}</div>`;
}

function _renderHistory() {
  if (!_releases.length) return "";
  const rows = _releases.slice().reverse().map((r) => {
    const active = _release && _release.release_id === r.release_id;
    const count = r.asset_count ?? 0;
    return `<div class="prom-rel-row${active ? " prom-rel-row-active" : ""}" data-rel="${esc(r.release_id)}"
        data-count="${esc(String(count))}" title="Use this release">
      <span class="ic ic-sm ic-history" aria-hidden="true"></span>
      <span class="prom-rel-row-id pl-mono">${esc(r.release_id)}</span>
      <span class="prom-rel-row-count">${esc(String(count))} asset${count === 1 ? "" : "s"}</span>
    </div>`;
  }).join("");
  return `<div class="prom-hist">
    <div class="prom-hist-head">Releases</div>
    ${rows}
  </div>`;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

function _rerender() {
  const body = _body();
  if (body) body.innerHTML = _render();
}

function _onChange(ev) {
  const sel = ev.target.closest("#promTarget");
  if (!sel) return;
  _selectedTargetId = sel.value;
  // A plan/result is target-specific — drop it when the target changes. The release is
  // family-scoped and stays valid across same-family targets.
  _plan = null;
  _runRec = null;
  _confirmText = "";
  _error = "";
  _rerender();
}

function _onInput(ev) {
  const input = ev.target.closest("#promConfirm");
  if (!input) return;
  _confirmText = input.value;
  // Toggle Apply without a full re-render so the input keeps focus.
  const target = _selectedTarget();
  const ok = !!_release && !!target && _confirmText.trim() === target.label && _busy === "";
  const applyBtn = document.getElementById("promApply");
  if (applyBtn) applyBtn.disabled = !ok;
}

function _onClick(ev) {
  if (ev.target.closest("#promRefresh")) { refreshPromotion(); return; }
  if (ev.target.closest("#promSnapshot:not([disabled])")) { _doSnapshot(); return; }
  if (ev.target.closest("#promPlan:not([disabled])")) { _doPlan(); return; }
  if (ev.target.closest("#promApply:not([disabled])")) { _doApply(); return; }

  // "Undo this run" button: drive the shared undo dialog scoped to this promotion run.
  // openUndoDialog spreads the selector and enforces the prod typed-confirm gate, so
  // reuse it exactly as timeline.js does (no panel-side undo logic).
  if (ev.target.closest("#promUndoRun")) {
    if (_runRec && _runRec.run_id) {
      openUndoDialog({ run_id: _runRec.run_id }, refreshPromotion);
    }
    return;
  }

  const relRow = ev.target.closest(".prom-rel-row");
  if (relRow) {
    const rid = relRow.getAttribute("data-rel");
    const count = Number(relRow.getAttribute("data-count") || 0);
    if (rid) {
      _release = { release_id: rid, asset_count: count };
      _plan = null; _runRec = null; _error = "";
      _rerender();
    }
  }
}

async function _doSnapshot() {
  if (!_source || !_source.id) return;
  _busy = "snapshot"; _error = ""; _plan = null; _runRec = null;
  _rerender();
  setPanelLoading("promotion", true);
  try {
    const res = await api.promotionSnapshot(_source.id, {});
    _release = { release_id: res.release_id, asset_count: res.asset_count };
    // Reflect the new release in the history list immediately (best-effort refresh after).
    await refreshReleasesQuietly();
  } catch (e) {
    _error = String((e && e.message) || e);
  } finally {
    _busy = "";
    setPanelLoading("promotion", false);
    _rerender();
  }
}

async function _doPlan() {
  const target = _selectedTarget();
  if (!target || !_release) return;
  _busy = "plan"; _error = ""; _runRec = null;
  _rerender();
  setPanelLoading("promotion", true);
  try {
    _plan = await api.promotionPlanHosted(target.id, _release.release_id);
  } catch (e) {
    _error = String((e && e.message) || e);
    _plan = null;
  } finally {
    _busy = "";
    setPanelLoading("promotion", false);
    _rerender();
  }
}

async function _doApply() {
  const target = _selectedTarget();
  if (!target || !_release) return;
  const isProd = _rung(target.env_role) === RUNG.prod;
  const opts = {};
  // PROD gate: the server requires the typed target label verbatim (require_prod_confirmation).
  if (isProd) opts.confirm = _confirmText.trim();
  _busy = "apply"; _error = "";
  _rerender();
  setPanelLoading("promotion", true);
  try {
    _runRec = await api.promotionApplyHosted(target.id, _release.release_id, opts);
    await refreshReleasesQuietly();
  } catch (e) {
    _error = String((e && e.message) || e);
    _runRec = null;
  } finally {
    _busy = "";
    setPanelLoading("promotion", false);
    _rerender();
  }
}

// Reload just the release history (after snapshot/apply) without disturbing the rest of the
// panel state. Best-effort — failures are swallowed.
async function refreshReleasesQuietly() {
  try {
    const relData = await api.promotionReleases();
    _releases = (relData && relData.releases) || [];
  } catch {
    /* best effort */
  }
}
